import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

export const fetchPaymentMethods = createAsyncThunk(
  'paymentMethods/fetchPaymentMethods',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/payment-methods`)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const createPaymentMethod = createAsyncThunk(
  'paymentMethods/createPaymentMethod',
  async ({ companyId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.post(`/companies/${companyId}/payment-methods`, payload)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const updatePaymentMethod = createAsyncThunk(
  'paymentMethods/updatePaymentMethod',
  async ({ companyId, methodId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!methodId) throw new Error('methodId is required')
      const response = await axiosClient.patch(
        `/companies/${companyId}/payment-methods/${methodId}`,
        payload
      )
      return { data: response.data, companyId: String(companyId), methodId: String(methodId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  items: [],
  status: 'idle',
  error: null,
  listCompanyId: null,

  saveStatus: 'idle',
  saveError: null,
}

export const slice = createSlice({
  name: 'paymentMethods',
  initialState,

  reducers: {
    resetPaymentMethodsState(state) {
      state.items = []
      state.status = 'idle'
      state.error = null
      state.listCompanyId = null
    },
    resetPaymentMethodsSaveState(state) {
      state.saveStatus = 'idle'
      state.saveError = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentMethods.pending, (state, action) => {
        state.status = 'loading'
        state.error = null
        state.listCompanyId = String(action.meta.arg?.companyId ?? '')
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, { payload }) => {
        state.status = 'succeeded'
        state.items = Array.isArray(payload?.data) ? payload.data : []
        state.listCompanyId = payload?.companyId ?? state.listCompanyId
      })
      .addCase(fetchPaymentMethods.rejected, (state, { payload }) => {
        state.status = 'failed'
        state.error = payload || 'Failed to fetch payment methods'
      })

      .addCase(createPaymentMethod.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(createPaymentMethod.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        if (payload?.data) state.items = [...state.items, payload.data]
      })
      .addCase(createPaymentMethod.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to create payment method'
      })

      .addCase(updatePaymentMethod.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(updatePaymentMethod.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        const methodId = String(payload?.methodId ?? '')
        const idx = state.items.findIndex((x) => String(x?.id) === methodId)
        if (idx >= 0 && payload?.data) state.items[idx] = payload.data
      })
      .addCase(updatePaymentMethod.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to update payment method'
      })
  },
})

export const { resetPaymentMethodsState, resetPaymentMethodsSaveState } = slice.actions

export default slice.reducer
